import { CheckIcon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Typography } from '@/components/ui/typography'
import { useNewsletterForm } from './useNewsletterForm'
import type { NewsletterPopupVariantProps } from './NewsletterPopup'
import newsletterImg from '@/assets/home/newsletter.webp'

export function NewsletterPopupDesktop({ onClose, onSubscribed }: NewsletterPopupVariantProps) {
  const { email, setEmail, status, submit } = useNewsletterForm(onSubscribed)
  const isSuccess = status === 'success'

  return (
    <div className="relative grid w-[820px] max-w-full grid-cols-2 overflow-hidden rounded-2xl bg-white shadow-2xl">
      <button
        type="button"
        onClick={onClose}
        aria-label="Zavřít"
        className="absolute top-3 right-3 z-10 rounded-full bg-white/80 p-1.5 text-slate-700 backdrop-blur-sm transition-colors hover:bg-white"
      >
        <XIcon className="h-5 w-5" />
      </button>

      <div className="relative">
        <img
          src={newsletterImg}
          alt="Pár zachumlaný v dece na terase chalupy nad podzimními horami"
          className="h-full min-h-[440px] w-full object-cover object-[30%_50%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-8 text-white">
          <span className="bg-red mb-3 inline-block rounded-full px-3 py-1 text-xs font-semibold">
            Každou středu přímo do e-mailu
          </span>
          <p className="text-3xl leading-tight font-bold">Nejlepší last-minute víkendovky</p>
        </div>
      </div>

      <div className="flex flex-col justify-center p-10">
        {isSuccess ? (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
              <CheckIcon className="h-7 w-7 text-green-700" />
            </div>
            <Typography variant="h3" className="mb-3">
              Jsi v klubu!
            </Typography>
            <p className="text-slate-900/60">Od teď ti každou středu pošleme nejlepší tipy, kam vyrazit o víkendu.</p>
          </div>
        ) : (
          <>
            <span className="text-red mb-3 text-xs font-semibold tracking-[0.14em] uppercase">
              Tipy na víkend zdarma
            </span>
            <Typography variant="h3" className="mb-3">
              Plány na víkend vyřešené
            </Typography>
            <p className="mb-6 text-slate-900/60">
              Ručně vybíráme chaty, wellness pobyty i tipy na výlety, kam o víkendu vyrazit. Jeden e-mail týdně, žádný
              spam.
            </p>
            <form onSubmit={submit} className="space-y-3">
              <Input
                type="email"
                name="email"
                required
                // eslint-disable-next-line jsx-a11y/no-autofocus -- the popup is a modal whose only task is this field; focusing it on open is deliberate (desktop only, no on-screen keyboard)
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Zadejte Váš e-mail"
                className="h-11"
              />
              <Button type="submit" className="h-11 w-full text-base" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Odesílám…' : 'Chci tipy na víkend'}
              </Button>
              {status === 'invalid-email' && <p className="text-red text-sm">Zkontrolujte prosím e-mailovou adresu.</p>}
              {status === 'error' && <p className="text-red text-sm">Něco se nepovedlo, zkuste to prosím znovu.</p>}
            </form>
            <div className="mt-4 flex items-center gap-1.5 text-sm text-slate-900/50">
              <CheckIcon className="h-4 w-4 shrink-0" />
              <span>Zdarma · 1 e-mail týdně · kdykoliv se odhlásíš</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
